import React, { useState } from 'react';
import { 
  Box, 
  CircularProgress, 
  TextField, 
  InputAdornment,
  Paper,
  Typography,
  useTheme
} from '@mui/material';
import { 
  DataGrid, 
  GridColDef, 
  GridToolbar,
  GridFilterModel,
  GridSortModel,
  GridRenderCellParams,
  GridRowHeightParams
} from '@mui/x-data-grid';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import SearchIcon from '@mui/icons-material/Search';
import { Request } from '../types/request';

interface RequestHistoryTableProps {
  requests: Request[];
  loading: boolean;
}

const RequestHistoryTable: React.FC<RequestHistoryTableProps> = ({ requests, loading }) => {
  const [searchText, setSearchText] = useState('');
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [rowHeight, setRowHeight] = useState<number>(60);
  const theme = useTheme();
  
  // Handle row height change
  const handleRowHeightChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newHeight = parseInt(event.target.value, 10);
    if (!isNaN(newHeight) && newHeight >= 40 && newHeight <= 200) {
      setRowHeight(newHeight);
    }
  };

  // Format the timestamp for display
  const formatTimestamp = (timestamp: string) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
      });
    } catch (e) {
      return timestamp; // Fallback to original value if parsing fails
    }
  };

  // Format the result for display
  const formatResult = (result: any) => {
    if (!result) return '';
    try {
      return JSON.stringify(result, null, 2);
    } catch (e) {
      return String(result);
    }
  };

  // Status cell renderer with colored background
  const StatusCell = (props: { value: string }) => {
    const statusMap: Record<string, { icon: string; color: string }> = {
      completed: { icon: '✓', color: '#7AB98E' },
      processing: { icon: '⚙️', color: '#E3A76F' },
      pending: { icon: '⏳', color: '#D4B483' },
      failed: { icon: '❌', color: '#C17C7C' },
      review: { icon: '🧐', color: '#E3A76F' }
    };

    const style = statusMap[props.value] || { icon: '', color: 'inherit' };

    return (
      <Box sx={{ backgroundColor: style.color, padding: 1, borderRadius: 1, textAlign: 'center' }}>
        {style.icon} {props.value}
      </Box>
    );
  };

  // Define columns for DataGrid
  const columns: GridColDef[] = [
    { 
      field: 'job_id', 
      headerName: 'Job ID', 
      flex: 1,
      minWidth: 150,
      filterable: true,
      sortable: true,
      resizable: true,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ padding: '8px 12px', width: '100%' }}>
          {params.value}
        </Box>
      )
    },
    { 
      field: 'document_type', 
      headerName: 'Request type', 
      flex: 1,
      minWidth: 120,
      filterable: true,
      sortable: true,
      resizable: true,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ padding: '8px 12px', width: '100%' }}>
          {params.value}
        </Box>
      )
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      flex: 1,
      minWidth: 120,
      filterable: true,
      sortable: true,
      resizable: true,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ padding: '0 8px' }}>
          <StatusCell value={params.value} />
        </Box>
      )
    },
    { 
      field: 'created_at', 
      headerName: 'Submission timestamp', 
      flex: 1,
      minWidth: 180,
      filterable: true,
      sortable: true,
      resizable: true,
      valueFormatter: (params: { value: any }) => formatTimestamp(params.value),
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ 
          width: '100%', 
          height: '100%', 
          overflow: 'auto',
          padding: '8px 12px',
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: '4px'
          }
        }}>
          {formatTimestamp(params.value)}
        </Box>
      )
    },
    { 
      field: 'consensus_result', 
      headerName: 'Results', 
      flex: 2,
      minWidth: 250,
      filterable: true,
      sortable: false,
      resizable: true,
      valueGetter: (params: { row: any }) => {
        // Safely handle the case where params or params.row might be undefined
        if (!params || !params.row) return '';
        
        // Try to get consensus_result, fallback to ai_result.result for backward compatibility,
        // and finally fall back to status if neither exists
        return params.row.consensus_result || 
               (params.row.ai_result?.result) || 
               (params.row.status === 'completed' ? 'Processing results...' : params.row.status);
      },
      valueFormatter: (params: { value: any }) => formatResult(params.value),
      renderCell: (params: GridRenderCellParams) => {
        const value = params.row.consensus_result || 
                      (params.row.ai_result?.result) || 
                      (params.row.status === 'completed' ? 'Processing results...' : params.row.status);
                      
        return (
          <Box sx={{ 
            width: '100%', 
            height: '100%', 
            overflow: 'auto',
            padding: '8px 12px',
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            '&::-webkit-scrollbar': {
              width: '6px',
              height: '6px'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '4px'
            }
          }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {formatResult(value)}
            </pre>
          </Box>
        );
      }
    }
  ];

  // Prepare rows with unique IDs for DataGrid
  const rows = requests.map((request, index) => ({
    id: request.job_id || index,
    ...request
  }));

  // Filter rows based on search text
  const filteredRows = searchText.trim() === '' ? rows : rows.filter((row) => {
    return Object.entries(row).some(([key, value]) => {
      if (key === 'id' || key === 'metadata' || key === 'ai_result' || key === 'consensus_result') return false;
      return String(value).toLowerCase().includes(searchText.toLowerCase());
    });
  });

  return (
    <Paper elevation={2} sx={{ height: 600, width: '100%' }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DragHandleIcon fontSize="small" />
            <Typography variant="body2">Row height:</Typography>
            <TextField
              type="number"
              size="small"
              value={rowHeight}
              onChange={handleRowHeightChange}
              InputProps={{
                inputProps: { min: 40, max: 200 }
              }}
              sx={{ width: 70 }}
            />
            <Typography variant="body2">px</Typography>
          </Box>
        </Box>
        <TextField
          variant="outlined"
          placeholder="Search in all columns..."
          size="small"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
      </Box>
      
      <DataGrid
        rows={filteredRows}
        columns={columns}
        loading={loading}
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        disableRowSelectionOnClick
        autoHeight
        rowHeight={rowHeight}
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 },
          },
        }}
        slots={{
          toolbar: GridToolbar,
          loadingOverlay: () => (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: '20px' }}>
              <CircularProgress />
            </Box>
          ),
          noRowsOverlay: () => (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: '20px' }}>
              <Typography>No requests found</Typography>
            </Box>
          ),
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: false,
          },
        }}
        sx={{
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: theme => theme.palette.mode === 'dark' ? '#333' : '#f5f5f5',
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 'bold',
            },
          },
          '& .MuiDataGrid-cell': {
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            lineHeight: '1.2',
            alignItems: 'center',
            padding: '0',
          },
          '& .MuiDataGrid-row': {
            '&:nth-of-type(odd)': {
              backgroundColor: theme => theme.palette.mode === 'dark' ? '#333333' : '#f9f9f9',
            },
          },
          '& .MuiDataGrid-columnSeparator': {
            visibility: 'visible',
            color: theme => theme.palette.divider,
          },
        }}
      />
    </Paper>
  );
};

export default RequestHistoryTable;